import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Badge } from '../../styles/v2.styled';
import { checkSession } from '../../../Common/Utils/SsoSession';

const BOARD_TYPES = [
  { id: 'Developer', label: '프로그래밍' },
  { id: 'Modeler', label: '모델링' },
  { id: 'Design', label: '디자인' },
  { id: 'Infra', label: '인프라' },
  { id: 'Media', label: '미디어' },
  { id: 'Plan', label: '기획/설계' },
  { id: 'Work', label: '업무일지' },
];

const PAGE_SIZE = 10;

const Shell = styled.div`
  display: grid;
  gap: 18px;
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.neutral[50]};

  strong {
    display: block;
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-size: 1.45rem;
    line-height: 1;
    margin-bottom: 8px;
  }

  span {
    color: ${({ theme }) => theme.colors.neutral[500]};
    font-size: 0.88rem;
  }
`;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Search = styled.input`
  min-height: 42px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 0 14px;
  color: ${({ theme }) => theme.colors.neutral[900]};
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Button = styled.button`
  min-height: 40px;
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.primary[600] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary[600] : theme.colors.neutral[0])};
  color: ${({ $primary, theme }) => ($primary ? theme.colors.neutral[0] : theme.colors.neutral[700])};
  -webkit-text-fill-color: ${({ $primary, theme }) => ($primary ? theme.colors.neutral[0] : theme.colors.neutral[700])};
  padding: 0 14px;
  font-weight: 900;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
`;

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TabButton = styled.button`
  min-height: 38px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary[400] : theme.colors.neutral[200])};
  border-radius: 8px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary[50] : theme.colors.neutral[0])};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary[700] : theme.colors.neutral[700])};
  -webkit-text-fill-color: ${({ $active, theme }) => ($active ? theme.colors.primary[700] : theme.colors.neutral[700])};
  padding: 0 13px;
  font-weight: 900;
  cursor: pointer;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral[50]};
`;

const Select = styled.select`
  min-height: 38px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 0 12px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  color: ${({ theme }) => theme.colors.neutral[800]};
`;

const Segments = styled.div`
  display: inline-flex;
  gap: 6px;
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const Segment = styled.button`
  min-height: 32px;
  border: 0;
  border-radius: 6px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary[600] : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.neutral[0] : theme.colors.neutral[600])};
  -webkit-text-fill-color: ${({ $active, theme }) => ($active ? theme.colors.neutral[0] : theme.colors.neutral[600])};
  padding: 0 12px;
  font-weight: 900;
  cursor: pointer;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 18px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const PostButton = styled.button`
  width: 100%;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary[400] : theme.colors.neutral[200])};
  border-radius: 12px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary[50] : theme.colors.neutral[0])};
  padding: 18px 20px;
  text-align: left;
  cursor: pointer;
  position: relative;
  transition: all 0.18s ease;
  box-shadow: ${({ $active }) => ($active ? '0 6px 16px rgba(8, 145, 178, 0.12)' : '0 1px 2px rgba(15, 23, 42, 0.04)')};

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.colors.primary[400]};
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  }
`;

const PostMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.78rem;

  > span {
    color: ${({ theme }) => theme.colors.neutral[500]};
    font-weight: 600;
  }
  > span:nth-of-type(1) {
    color: ${({ theme }) => theme.colors.neutral[700]};
    font-weight: 700;
  }
`;

const PostTitle = styled.div`
  color: ${({ theme }) => theme.colors.neutral[900]};
  font-weight: 800;
  font-size: 1.05rem;
  line-height: 1.4;
  margin-bottom: 8px;
  letter-spacing: -0.2px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const PostPreview = styled.div`
  color: ${({ theme }) => theme.colors.neutral[700]};
  line-height: 1.6;
  font-size: 0.88rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
`;

const SidePanel = styled.aside`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  min-height: 320px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  position: sticky;
  top: 90px;
`;

const PanelHead = styled.div`
  padding: 26px 26px 22px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%);
`;

const PanelTitle = styled.h3`
  margin: 12px 0 0;
  color: ${({ theme }) => theme.colors.neutral[900]};
  font-size: 1.5rem;
  font-weight: 900;
  line-height: 1.3;
  letter-spacing: -0.5px;
`;

const PanelBody = styled.div`
  padding: 26px;
`;

const HtmlBody = styled.div`
  color: ${({ theme }) => theme.colors.neutral[800]};
  font-size: 0.97rem;
  line-height: 1.85;
  word-break: break-word;
  letter-spacing: -0.1px;

  /* 단락/리스트 간격 */
  > * + * { margin-top: 12px; }
  p, ul, ol, blockquote, pre, table { margin: 0 0 12px; }
  p { color: ${({ theme }) => theme.colors.neutral[800]}; }

  /* 헤딩 */
  h1, h2, h3, h4 {
    color: ${({ theme }) => theme.colors.neutral[900]};
    font-weight: 800;
    line-height: 1.4;
    margin: 18px 0 10px;
  }
  h1 { font-size: 1.4rem; }
  h2 { font-size: 1.2rem; }
  h3 { font-size: 1.05rem; }

  /* 리스트 */
  ul, ol {
    padding-left: 22px;
    li { margin-bottom: 6px; line-height: 1.75; }
  }

  /* 링크 */
  a {
    color: ${({ theme }) => theme.colors.primary[700]};
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    &:hover { color: ${({ theme }) => theme.colors.primary[800] || '#155e75'}; }
  }

  /* 인라인 코드 */
  code {
    background: ${({ theme }) => theme.colors.neutral[100]};
    color: ${({ theme }) => theme.colors.neutral[900]};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.88em;
  }

  /* 코드 블록 */
  pre {
    background: ${({ theme }) => theme.colors.neutral[900]};
    color: #f1f5f9;
    padding: 16px 18px;
    border-radius: 8px;
    overflow-x: auto;
    line-height: 1.6;
    code {
      background: transparent;
      color: inherit;
      padding: 0;
      font-size: 0.88em;
    }
  }

  /* 인용 */
  blockquote {
    border-left: 4px solid ${({ theme }) => theme.colors.primary[300] || '#67e8f9'};
    background: ${({ theme }) => theme.colors.primary[50] || '#ecfeff'};
    padding: 12px 16px;
    border-radius: 0 8px 8px 0;
    color: ${({ theme }) => theme.colors.neutral[700]};
  }

  /* 강조 */
  strong, b { color: ${({ theme }) => theme.colors.neutral[900]}; font-weight: 800; }
  em, i { color: ${({ theme }) => theme.colors.neutral[800]}; }

  /* 미디어 */
  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 12px 0;
  }

  /* 테이블 */
  table {
    width: 100%;
    border-collapse: collapse;
    th, td {
      border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
      padding: 8px 10px;
      text-align: left;
    }
    th {
      background: ${({ theme }) => theme.colors.neutral[50]};
      font-weight: 700;
    }
  }

  /* 빈 줄도 일정 높이 유지 */
  br + br { display: block; content: ''; margin-top: 6px; }
`;

const Empty = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.neutral[300]};
  border-radius: 8px;
  padding: 34px 18px;
  color: ${({ theme }) => theme.colors.neutral[500]};
  text-align: center;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const Editor = styled.form`
  display: grid;
  gap: 12px;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  color: ${({ theme }) => theme.colors.neutral[700]};
  font-size: 0.86rem;
  font-weight: 900;
`;

const Input = styled.input`
  min-height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 0 12px;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const Textarea = styled.textarea`
  min-height: 180px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 12px;
  resize: vertical;
  color: ${({ theme }) => theme.colors.neutral[900]};
  line-height: 1.6;
`;

const CommentList = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 18px;
`;

const Comment = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  padding: 12px;

  strong {
    color: ${({ theme }) => theme.colors.neutral[900]};
  }

  p {
    margin: 8px 0;
    color: ${({ theme }) => theme.colors.neutral[700]};
    line-height: 1.55;
  }

  small {
    color: ${({ theme }) => theme.colors.neutral[500]};
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
`;

function normalizeKey(item, ...keys) {
  for (const key of keys) {
    if (item && item[key] !== undefined && item[key] !== null) return item[key];
  }
  return '';
}

function stripHtml(value = '') {
  return String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\n/g, '<br />');
}

async function postJson(url, body = {}, credentials = 'include') {
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  if (!response.ok || !text) return null;
  return JSON.parse(text);
}

function getEndpoints(type) {
  return {
    categories: `/request/${type}CategoryList`,
    list: `/request/Select${type}BoardPage`,
    pageList: `/request/Select${type}BoardPageList`,
    read: `/request/Read${type}Board`,
    increase: `/request/${type}SearchCountPlus`,
    insert: `/request/Insert${type}Board`,
    update: `/request/Update${type}Board`,
    remove: `/request/Delete${type}Board`,
    comments: `/request/Read${type}Comments`,
    insertComment: `/request/Insert${type}Comment`,
    removeComment: `/request/Delete${type}Comment`,
  };
}

function WorkspaceBoard() {
  const [session, setSession] = useState({});
  const [type, setType] = useState('Developer');
  const [categories, setCategories] = useState(['ALL']);
  const [category, setCategory] = useState('ALL');
  const [scope, setScope] = useState(true);
  const [query, setQuery] = useState('');
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState({ title: '', contents: '', category: 'ALL', PUBLIC: '공개' });
  const [message, setMessage] = useState('');

  const endpoints = useMemo(() => getEndpoints(type), [type]);
  const currentTypeLabel = BOARD_TYPES.find((item) => item.id === type)?.label || type;
  const isLoggedIn = session?.State === 'LOGIN';

  const loadCategories = useCallback(async () => {
    try {
      const data = await postJson(endpoints.categories);
      const next = Array.isArray(data)
        ? data.map((item) => normalizeKey(item, 'notice_category', 'NOTICE_CATEGORY')).filter(Boolean)
        : [];
      const unique = ['ALL', ...new Set(next)];
      setCategories(unique);
      setCategory('ALL');
      setForm((prev) => ({ ...prev, category: unique[1] || 'ALL' }));
    } catch (error) {
      setCategories(['ALL']);
      setCategory('ALL');
    }
  }, [endpoints.categories]);

  const loadPosts = useCallback(async (targetPage = page) => {
    const min = targetPage === 1 ? 0 : (targetPage - 1) * PAGE_SIZE + 1;
    const max = targetPage * PAGE_SIZE;
    try {
      const [listData, pageData] = await Promise.all([
        postJson(endpoints.list, { MinNumber: min, MaxNumber: max, IsPublic: scope, Category: category }),
        postJson(endpoints.pageList, { IsPublic: scope, Category: category }),
      ]);
      setPosts(Array.isArray(listData) ? listData : []);
      const pageInfo = Array.isArray(pageData) && pageData.length ? pageData[0] : null;
      const calculated = Number(normalizeKey(pageInfo, 'totalpage', 'TOTALPAGE')) + 1;
      setTotalPages(Number.isFinite(calculated) && calculated > 0 ? calculated : 1);
      setMessage('');
    } catch (error) {
      setPosts([]);
      setTotalPages(1);
      setMessage('데이터를 불러오지 못했습니다. 서버 연결 상태를 확인하세요.');
    }
  }, [category, endpoints.list, endpoints.pageList, page, scope]);

  useEffect(() => {
    checkSession().then((data) => setSession(data || {}));
  }, []);

  useEffect(() => {
    loadCategories();
    setSelectedPost(null);
    setEditorOpen(false);
    setPage(1);
  }, [loadCategories, type]);

  useEffect(() => {
    loadPosts(page);
  }, [loadPosts, page]);

  const filteredPosts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return posts;
    return posts.filter((post) => {
      const title = normalizeKey(post, 'notice_title', 'NOTICE_TITLE').toLowerCase();
      const name = normalizeKey(post, 'notice_name', 'NOTICE_NAME').toLowerCase();
      const contents = stripHtml(normalizeKey(post, 'notice_contents', 'NOTICE_CONTENTS')).toLowerCase();
      return title.includes(needle) || name.includes(needle) || contents.includes(needle);
    });
  }, [posts, query]);

  const openPost = async (post) => {
    const num = normalizeKey(post, 'num', 'NUM');
    try {
      const data = await postJson(endpoints.read, { num }, 'same-origin');
      const detail = Array.isArray(data) && data.length ? data[0] : post;
      const commentData = await postJson(endpoints.comments, { num }, 'same-origin');
      await postJson(endpoints.increase, { num }, 'same-origin').catch(() => null);
      setSelectedPost(detail);
      setComments(Array.isArray(commentData) ? commentData : []);
      setEditorOpen(false);
    } catch (error) {
      setSelectedPost(post);
      setComments([]);
    }
  };

  const openEditor = (post = null) => {
    if (post) {
      setSelectedPost(post);
      setForm({
        num: normalizeKey(post, 'num', 'NUM'),
        title: normalizeKey(post, 'notice_title', 'NOTICE_TITLE'),
        contents: stripHtml(normalizeKey(post, 'notice_contents', 'NOTICE_CONTENTS')),
        category: normalizeKey(post, 'notice_category', 'NOTICE_CATEGORY') || categories[1] || 'ALL',
        PUBLIC: normalizeKey(post, 'public', 'PUBLIC') === 0 ? '비공개' : '공개',
      });
    } else {
      setForm({ title: '', contents: '', category: categories[1] || 'ALL', PUBLIC: '공개' });
    }
    setEditorOpen(true);
  };

  const submitPost = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.contents.trim()) return;
    const body = {
      num: form.num,
      title: form.title.trim(),
      contents: escapeHtml(form.contents.trim()),
      category: form.category === 'ALL' ? currentTypeLabel : form.category,
      PUBLIC: form.PUBLIC,
    };
    try {
      await postJson(form.num ? endpoints.update : endpoints.insert, body);
      setEditorOpen(false);
      setSelectedPost(null);
      await loadPosts(1);
      setPage(1);
    } catch (error) {
      setMessage('저장하지 못했습니다. 로그인 상태와 서버 연결을 확인하세요.');
    }
  };

  const removePost = async (post) => {
    const num = normalizeKey(post, 'num', 'NUM');
    if (!num) return;
    try {
      await postJson(endpoints.remove, { num }, 'same-origin');
      setSelectedPost(null);
      await loadPosts(page);
    } catch (error) {
      setMessage('삭제하지 못했습니다.');
    }
  };

  const submitComment = async () => {
    const num = normalizeKey(selectedPost, 'num', 'NUM');
    if (!num || !commentText.trim()) return;
    try {
      await postJson(endpoints.insertComment, { noticeNum: num, commentText: commentText.trim() });
      setCommentText('');
      const commentData = await postJson(endpoints.comments, { num }, 'same-origin');
      setComments(Array.isArray(commentData) ? commentData : []);
    } catch (error) {
      setMessage('댓글을 등록하지 못했습니다.');
    }
  };

  return (
    <Shell>
      <TopGrid>
        <StatCard>
          <strong>{posts.length}</strong>
          <span>현재 목록</span>
        </StatCard>
        <StatCard>
          <strong>{currentTypeLabel}</strong>
          <span>선택 작업실</span>
        </StatCard>
        <StatCard>
          <strong>{isLoggedIn ? 'LOGIN' : 'READ'}</strong>
          <span>작성 권한 상태</span>
        </StatCard>
      </TopGrid>

      <TabList role="tablist" aria-label="직무별 작업 게시판">
        {BOARD_TYPES.map((item) => (
          <TabButton
            key={item.id}
            type="button"
            $active={item.id === type}
            onClick={() => setType(item.id)}
          >
            {item.label}
          </TabButton>
        ))}
      </TabList>

      <Toolbar>
        <Search
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="제목, 작성자, 내용 검색"
          aria-label="작업 게시글 검색"
        />
        <ButtonRow>
          <Button type="button" onClick={() => loadPosts(page)}>새로고침</Button>
          <Button type="button" $primary onClick={() => openEditor()} disabled={!isLoggedIn}>
            새 작업 등록
          </Button>
        </ButtonRow>
      </Toolbar>

      <FilterBar>
        <ButtonRow>
          <Select value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }}>
            {categories.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </Select>
          <Segments aria-label="공개 범위">
            <Segment type="button" $active={scope === true} onClick={() => { setScope(true); setPage(1); }}>
              공개
            </Segment>
            <Segment type="button" $active={scope === false} onClick={() => { setScope(false); setPage(1); }}>
              내 작업
            </Segment>
          </Segments>
        </ButtonRow>
        {message ? <Badge $variant="warning">{message}</Badge> : <Badge $variant="default">총 {filteredPosts.length}개 표시</Badge>}
      </FilterBar>

      {editorOpen && (
        <SidePanel>
          <PanelHead>
            <Badge $variant="primary">{form.num ? '수정' : '신규'}</Badge>
            <PanelTitle>작업 글쓰기</PanelTitle>
          </PanelHead>
          <PanelBody>
            <Editor onSubmit={submitPost}>
              <Field>
                제목
                <Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
              </Field>
              <Field>
                카테고리
                <Select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                  {categories.filter((item) => item !== 'ALL').map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                  {categories.length <= 1 && <option value={currentTypeLabel}>{currentTypeLabel}</option>}
                </Select>
              </Field>
              <Field>
                공개 범위
                <Select value={form.PUBLIC} onChange={(event) => setForm({ ...form, PUBLIC: event.target.value })}>
                  <option value="공개">공개</option>
                  <option value="비공개">비공개</option>
                </Select>
              </Field>
              <Field>
                내용
                <Textarea value={form.contents} onChange={(event) => setForm({ ...form, contents: event.target.value })} />
              </Field>
              <ButtonRow>
                <Button type="submit" $primary>저장</Button>
                <Button type="button" onClick={() => setEditorOpen(false)}>취소</Button>
              </ButtonRow>
            </Editor>
          </PanelBody>
        </SidePanel>
      )}

      <ContentGrid>
        <div>
          {filteredPosts.length === 0 ? (
            <Empty>표시할 작업 글이 없습니다. 필터를 바꾸거나 새 작업을 등록하세요.</Empty>
          ) : (
            <List>
              {filteredPosts.map((post) => {
                const num = normalizeKey(post, 'num', 'NUM');
                const title = normalizeKey(post, 'notice_title', 'NOTICE_TITLE') || '제목 없음';
                const writer = normalizeKey(post, 'notice_name', 'NOTICE_NAME') || '-';
                const itemCategory = normalizeKey(post, 'notice_category', 'NOTICE_CATEGORY') || currentTypeLabel;
                const count = normalizeKey(post, 'search_count', 'SEARCH_COUNT') || 0;
                const preview = stripHtml(normalizeKey(post, 'notice_contents', 'NOTICE_CONTENTS')).slice(0, 110);
                return (
                  <PostButton
                    key={num || title}
                    type="button"
                    $active={normalizeKey(selectedPost, 'num', 'NUM') === num}
                    onClick={() => openPost(post)}
                  >
                    <PostMeta>
                      <Badge $variant="default">{itemCategory}</Badge>
                      <span>{writer}</span>
                      <span>조회 {count}</span>
                    </PostMeta>
                    <PostTitle>{title}</PostTitle>
                    <PostPreview>{preview || '상세 내용을 열어 확인하세요.'}</PostPreview>
                  </PostButton>
                );
              })}
            </List>
          )}
          <Pager>
            {Array.from({ length: totalPages }, (_, index) => index + 1).slice(0, 10).map((item) => (
              <Button key={item} type="button" $primary={item === page} onClick={() => setPage(item)}>
                {item}
              </Button>
            ))}
          </Pager>
        </div>

        <SidePanel>
          {selectedPost ? (
            <>
              <PanelHead>
                <Badge $variant="primary">{normalizeKey(selectedPost, 'notice_category', 'NOTICE_CATEGORY') || currentTypeLabel}</Badge>
                <PanelTitle>{normalizeKey(selectedPost, 'notice_title', 'NOTICE_TITLE') || '제목 없음'}</PanelTitle>
                <PostMeta>
                  <span>{normalizeKey(selectedPost, 'notice_name', 'NOTICE_NAME') || '-'}</span>
                  <span>조회 {normalizeKey(selectedPost, 'search_count', 'SEARCH_COUNT') || 0}</span>
                </PostMeta>
              </PanelHead>
              <PanelBody>
                <HtmlBody dangerouslySetInnerHTML={{ __html: normalizeKey(selectedPost, 'notice_contents', 'NOTICE_CONTENTS') || '내용이 없습니다.' }} />
                <ButtonRow style={{ marginTop: 18 }}>
                  <Button type="button" onClick={() => openEditor(selectedPost)} disabled={!isLoggedIn}>수정</Button>
                  <Button type="button" onClick={() => removePost(selectedPost)} disabled={!isLoggedIn}>삭제</Button>
                </ButtonRow>
                <CommentList>
                  <strong>댓글 {comments.length}개</strong>
                  {comments.map((comment) => (
                    <Comment key={normalizeKey(comment, 'comment_num', 'COMMENT_NUM')}>
                      <strong>{normalizeKey(comment, 'user_name', 'USER_NAME') || '-'}</strong>
                      <p>{normalizeKey(comment, 'contents', 'CONTENTS')}</p>
                      <small>{normalizeKey(comment, 'create_time', 'CREATE_TIME')}</small>
                    </Comment>
                  ))}
                  <Field>
                    댓글 작성
                    <Textarea
                      style={{ minHeight: 84 }}
                      value={commentText}
                      onChange={(event) => setCommentText(event.target.value)}
                      disabled={!isLoggedIn}
                      placeholder={isLoggedIn ? '댓글을 입력하세요.' : '로그인 후 댓글을 작성할 수 있습니다.'}
                    />
                  </Field>
                  <Button type="button" $primary onClick={submitComment} disabled={!isLoggedIn || !commentText.trim()}>
                    댓글 등록
                  </Button>
                </CommentList>
              </PanelBody>
            </>
          ) : (
            <PanelBody>
              <Empty>왼쪽 목록에서 작업 글을 선택하면 상세 내용과 댓글을 확인할 수 있습니다.</Empty>
            </PanelBody>
          )}
        </SidePanel>
      </ContentGrid>
    </Shell>
  );
}

export default WorkspaceBoard;
